import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    FormMultiselectDropdown,
    FormTextField,
    NamedProperty,
    ValidationResult,
    FormValidationSummary,
    Checkbox
} from "sprint-common-web";

import { FormDistributorSearchDropdown } from "../../../../../../common/components/form/FormDistributorDropdown";
import { VersionType } from "../../../../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { EditEpisodeVersionFormInputs } from "./EditEpisodeVersionFormInputs";
import { useEditEpisodeVersionForm } from "./useEditEpisodeVersionForm";

export interface EditEpisodeVersionFormProps {
    selectedEpisodeVersionId: string;
    onCancel(): void;
    onSubmit(params: UpdateEpisodeVersionParams): void;
}

export interface UpdateEpisodeVersionParams {
    versionId: string;
    distributor: NamedProperty;
    type: VersionType;
    code?: string;
    isTrailer: boolean;
}

export const EditEpisodeVersionForm: React.FunctionComponent<EditEpisodeVersionFormProps> = (
    props: EditEpisodeVersionFormProps
) => {
    const {
        versionTypes,
        trailerVersionTypes,
        airlines,
        selectedEpisodeVersion,
        isTrailerSelected,
        setIsTrailerSelected,
        defaultValues,
        defaultOptions,
        validateEditEpisodeVersion,
        editEpisodeVersion
    } = useEditEpisodeVersionForm(props.selectedEpisodeVersionId);

    const [validationResult, setValidationResult] = useState<ValidationResult>();

    const { errors, control, formState, handleSubmit } = useForm<EditEpisodeVersionFormInputs>({
        defaultValues,
        criteriaMode: "all"
    });

    const handleEditEpisodeVersion = async (inputs: EditEpisodeVersionFormInputs) => {
        setValidationResult(undefined);

        const result = await validateEditEpisodeVersion(inputs);
        setValidationResult(result);

        if (!result.isValid) {
            return;
        }

        const updatedVersion = await editEpisodeVersion(inputs);

        props.onSubmit(updatedVersion);
    };

    const handleCancelClick = () => {
        props.onCancel();
    };

    const handleTrailerVersionToggle = (_: unknown, isChecked?: boolean) => {
        if (!trailerVersionTypes) return;

        setIsTrailerSelected(isChecked ?? false);
        control.setValue("versionTypeId", isChecked ? trailerVersionTypes[0].id : undefined);
    };

    return (
        <Form onSubmit={handleSubmit(handleEditEpisodeVersion)}>
            <FormFields>
                <FormDistributorSearchDropdown
                    initOptions={defaultOptions.distributors}
                    control={control}
                    errors={errors}
                    rules={{ required: "Search and select distributor" }}
                    name="distributorId"
                    label="Distributor"
                    placeholder={"Distributor"}
                    disabled={!!selectedEpisodeVersion?.buzzVersion}
                />

                <FormDropdown
                    options={versionTypes?.map((item) => mapToDropdownOption(item)) ?? []}
                    label="Version Type"
                    control={control}
                    name="versionTypeId"
                    errors={errors}
                    rules={{ required: "Search and select version type" }}
                    disabled={isTrailerSelected || !!selectedEpisodeVersion?.buzzVersion}
                />

                <FormTextField
                    label="Code"
                    name="code"
                    control={control}
                    errors={errors}
                    disabled={!!selectedEpisodeVersion?.buzzVersion}
                />

                <FormMultiselectDropdown
                    options={airlines.map((item) => mapToDropdownOption(item))}
                    control={control}
                    name="brandingIds"
                    errors={errors}
                    label="Brandings"
                    disabled={true}
                />

                <Checkbox
                    label="Trailer version"
                    name="isTrailerSelected"
                    checked={isTrailerSelected}
                    onChange={handleTrailerVersionToggle}
                    testId="trailer-version-selected"
                    disabled={!!selectedEpisodeVersion?.buzzVersion}
                />
            </FormFields>
            {validationResult && <FormValidationSummary validationResult={validationResult} />}
            <FormActions isLoading={formState.isSubmitting || formState.isValidating} onCancel={handleCancelClick} />
        </Form>
    );
};
